import React, { type RefObject } from 'react';
import styled from 'styled-components';
import SectionContainer from '../SectionContainer';
import EditableText from '~/components/organism/EditableText';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import {
  useRecoilState,
  useSetRecoilState,
  type SetterOrUpdater,
} from 'recoil';
import type {
  FormBuilder_Event_Field,
  FormBuilder_NodeFragment,
  FormBuilder_ScreenNode,
} from '~/graphql/types';
import { Body } from '~/components/atom/Typography';
import Icon from '~/components/atom/Icon';
import { autoGeneratedEvent } from '~/components/page/Forms/components/Builder/state/autoGeneratedEvent';
import { eventsState } from '~/components/page/Forms/components/Builder/state/nodesAndEvents';
import getEventFieldForBlock from '~/components/page/Forms/components/Builder/utils/autoGeneratedEventUtils/getEventFieldForBlock';
import isBlockWithOutput, {
  type BlockWithOutput,
} from '~/components/page/Forms/components/Builder/utils/typeguards/isBlockWithOutput';
import usePrevious from '~/hooks/usePrevious';

const text = {
  cancel: 'Afbreken',
  save: 'Opslaan',
  unresolvedIssues: 'Er zijn nog onopgeloste problemen',
};

export type Props = {
  nodes: Array<FormBuilder_NodeFragment>;
  node: FormBuilder_ScreenNode;
  setNodeState: SetterOrUpdater<FormBuilder_ScreenNode | null>;
  initialState: RefObject<FormBuilder_ScreenNode>;
  hasIssues?: boolean;
  onCancel: () => void;
  onSave: (update: FormBuilder_ScreenNode) => void;
};

const TopBar: React.FCC<Props> = ({
  nodes,
  node,
  setNodeState,
  onSave,
  onCancel,
  initialState,
  hasIssues,
}) => {
  const [events, setEvents] = useRecoilState(eventsState);
  const setAutoGeneratedEvent = useSetRecoilState(autoGeneratedEvent);

  const prevName = usePrevious(node.name);

  const onChangeEventFieldName = (value: string) => {
    const updatedFields: Array<FormBuilder_Event_Field> = [];

    for (const block of node.blocks) {
      if (!isBlockWithOutput(block)) continue;

      const eventFieldForBlock = getEventFieldForBlock({
        blockKey: block.key,
        nodes,
        eventFields: events[0].fields,
      });

      if (!eventFieldForBlock) continue;

      if (
        prevName &&
        eventFieldForBlock.name !== getFieldLabel(prevName, block)
      ) {
        continue;
      }

      const updatedFieldForBlock: FormBuilder_Event_Field = {
        ...eventFieldForBlock,
        name: getFieldLabel(value, block),
      };

      updatedFields.push(updatedFieldForBlock);
    }

    setEvents(prev => {
      if (!prev || prev.length === 0) return prev;

      const updatedFieldKeys = updatedFields.map(({ key }) => key);
      const unchangedFields = prev[0].fields.filter(
        field => !updatedFieldKeys.includes(field.key),
      );

      return [
        {
          ...prev[0],
          fields: [...unchangedFields, ...updatedFields],
        },
      ];
    });
  };

  return (
    <SectionContainer gap="m" margin={[null, null, 'l', null]}>
      <EditableTextContainer>
        <EditableText
          as="h1"
          size="l"
          iconSize="m"
          className="typography"
          text={node?.name}
          onSave={name => {
            setNodeState(prev => (!prev ? prev : { ...prev, name }));
            setAutoGeneratedEvent({});
            onChangeEventFieldName(name);
          }}
          truncate
        />
      </EditableTextContainer>
      {hasIssues && (
        <JustificationContainer width="100%" justification="end" gap="xxs">
          <Icon name="error" color={{ group: 'danger' }} />
          <Body size="base" color={{ group: 'danger' }} margin={[null]}>
            {text.unresolvedIssues}
          </Body>
        </JustificationContainer>
      )}
      <JustificationContainer gap="m">
        <Button
          label={text.cancel}
          appearance="danger"
          ghost
          onClick={() => {
            setNodeState(prev => (!prev ? prev : initialState.current));
            onCancel();
          }}
        />
        <Button
          label={text.save}
          icon="save"
          appearance="secondary"
          onClick={() => {
            if (node) onSave(node);
          }}
        />
      </JustificationContainer>
    </SectionContainer>
  );
};

const getFieldLabel = (screenNodeName: string, block: BlockWithOutput) =>
  `${screenNodeName || ''} > ${block.label.nl || block.label.en}`;

const EditableTextContainer = styled.div`
  width: 70%;
`;

export default TopBar;
